import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { moduleService, accessRequestService } from '../services/api';

function AccessRequestForm() {
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [justification, setJustification] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingModules, setLoadingModules] = useState(true);
  const navigate = useNavigate();

  // Garantir que modules seja sempre um array
  const safeModules = Array.isArray(modules) ? modules : [];

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      console.log('Carregando módulos...');
      const data = await moduleService.listModules();
      console.log('Resposta da API:', data);
      
      // Garantir que sempre seja um array
      let modulesArray = [];
      
      if (Array.isArray(data)) {
        modulesArray = data;
      } else if (data && Array.isArray(data.content)) {
        modulesArray = data.content;
      } else if (data && Array.isArray(data.modules)) {
        modulesArray = data.modules;
      } else if (data) {
        // Se data não é array mas existe, pode ser um objeto único
        modulesArray = [];
        console.warn('Formato de resposta inesperado:', data);
        setError('Formato de resposta inesperado da API. Verifique o console para mais detalhes.');
      }
      
      console.log('Módulos processados:', modulesArray);
      setModules(modulesArray);
      
      if (modulesArray.length === 0 && !error) {
        setError('Nenhum módulo disponível no momento. Verifique se você está autenticado e se há módulos cadastrados.');
      } else if (modulesArray.length > 0) {
        setError(''); // Limpar erro se houver módulos
      }
    } catch (err) {
      console.error('Erro completo ao carregar módulos:', err);
      console.error('Status:', err.response?.status);
      console.error('Dados da resposta:', err.response?.data);
      
      let errorMessage = 'Erro ao carregar módulos. ';
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage += 'Você não está autenticado ou sua sessão expirou. Faça login novamente.';
      } else if (err.response?.status === 404) {
        errorMessage += 'Endpoint não encontrado. Verifique a URL da API.';
      } else if (err.response?.data) {
        errorMessage += err.response.data.message || JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Verifique sua conexão e tente novamente.';
      }
      
      setError(errorMessage);
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const handleModuleToggle = (moduleId) => {
    if (selectedModules.includes(moduleId)) {
      setSelectedModules(selectedModules.filter((id) => id !== moduleId));
    } else {
      if (selectedModules.length >= 3) {
        setError('Máximo de 3 módulos por solicitação');
        return;
      }
      setSelectedModules([...selectedModules, moduleId]);
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (selectedModules.length === 0) {
      setError('Selecione pelo menos um módulo');
      return;
    }

    if (justification.length < 20 || justification.length > 500) {
      setError('A justificativa deve ter entre 20 e 500 caracteres');
      return;
    }

    setLoading(true);

    try {
      await accessRequestService.createRequest(
        selectedModules,
        justification,
        urgent
      );
      navigate('/requests');
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data ||
        'Erro ao criar solicitação. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loadingModules) {
    return (
      <div className="container">
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando módulos...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Nova Solicitação de Acesso</h4>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label fw-bold">
                    Selecione os Módulos (máximo 3)
                  </label>
                  <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {safeModules.length === 0 ? (
                      <p className="text-muted">Nenhum módulo disponível</p>
                    ) : (
                      safeModules.map((module) => (
                        <div key={module.id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`module-${module.id}`}
                            checked={selectedModules.includes(module.id)}
                            onChange={() => handleModuleToggle(module.id)}
                            disabled={loading || (!selectedModules.includes(module.id) && selectedModules.length >= 3)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`module-${module.id}`}
                          >
                            <strong>{module.name}</strong>
                            {module.description && (
                              <div className="text-muted small">
                                {module.description}
                              </div>
                            )}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  <small className="text-muted">
                    Selecionados: {selectedModules.length}/3
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="justification" className="form-label fw-bold">
                    Justificativa
                  </label>
                  <textarea
                    className="form-control"
                    id="justification"
                    rows="5"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    required
                    minLength={20}
                    maxLength={500}
                    disabled={loading}
                    placeholder="Descreva a justificativa para o acesso (mínimo 20 caracteres)"
                  />
                  <small className="text-muted">
                    {justification.length}/500 caracteres
                  </small>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="urgent"
                      checked={urgent}
                      onChange={(e) => setUrgent(e.target.checked)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="urgent">
                      Solicitação Urgente
                    </label>
                  </div>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/requests')}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Enviando...' : 'Enviar Solicitação'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessRequestForm;

